// Painel "Saldos das UCs" (Gestão > Rateio) — solicitação #5954.
//
// Substitui a planilha da Brenda ("Cadastro Geral Consumidor Revisado.xlsx", aba
// "Revisão"): uma linha por UC do rateio, com o saldo de créditos, quantos meses
// de consumo ele paga, o % vigente, o % que o MODELO DA PLANILHA daria e as
// divergências que antes ela caçava no olho.
//
// A fonte do rateio é o Rateio Antigo (`rateios_credito_itens`), que é o que roda
// de verdade: o Simulador nunca fechou um rateio (tabela `rateios` vazia em
// 22/09/2026). O painel só LÊ o rateio — ajustar é registrar um pedido
// (`rateio_ajustes_uc`), e o % que vale continua sendo o da planilha que sobe.
//
// Arquivo puro (sem Supabase) para rodar em `node --test`:
//   node --experimental-strip-types --test views/Gestao/Rateio/__tests__/saldosUcs.test.ts

import {
  avaliarCoberturaSaldo,
  COBERTURA_MINIMA_SAUDAVEL_MESES,
  COBERTURA_MAXIMA_SAUDAVEL_MESES,
  COBERTURA_REINCLUIR_MESES,
  deveReincluirNoRateio,
  STATUS_FORA_DO_RATEIO,
  type CoberturaSaldo,
} from './coberturaSaldo.ts';
import { limparUc, mesReferenciaParaOrdinal, normalizarMesReferencia, rotuloDeOrdinal } from './mesReferenciaRateio.ts';
import { consumoOperacionalDaUc, type UcComConsumo } from '../../../src/lib/consumoDaUc.ts';
import { LIMIAR_TRAVAR_MESES, type FonteConsumo } from '../../../src/lib/consumoOperacional.ts';
import { custoDisponibilidadeDe } from '../../../src/lib/analiseTecnicaConsumo.ts';
// Só tipos — `consumoHistorico.ts` importa o cliente Supabase; `import type` some na compilação.
import type { FaturaConsumoResumo } from './consumoHistorico.ts';
import type { RateioAjusteUc } from '../../../types';

// ── Status do rateio ────────────────────────────────────────────────────────

/** Status da planilha em que a UC recebe crédito da usina. */
export const STATUS_RECEBENDO = ['Ativa', '1° Ciclo', '2° Ciclo'] as const;

/**
 * Mesma tradução de `sync_uc_from_rateio` (a RPC que o upload do Rateio Antigo
 * chama): a planilha vem com "ativa", "1o ciclo", "1º Ciclo"... Normalizar igual
 * ao banco evita o painel acusar divergência de status que é só grafia.
 */
export function normalizarStatusRateio(status: unknown): string {
  const s = String(status ?? '').trim();
  const baixo = s.toLowerCase();
  if (['ativa', 'ativo'].includes(baixo)) return 'Ativa';
  if (['ativa-s/-rateio', 'ativa s/ rateio', 'ativa sem rateio', 'ativa-s-rateio'].includes(baixo)) return 'Ativa-S/-Rateio';
  if (['1 ciclo', '1o ciclo', '1° ciclo', '1º ciclo'].includes(baixo)) return '1° Ciclo';
  if (['2 ciclo', '2o ciclo', '2° ciclo', '2º ciclo'].includes(baixo)) return '2° Ciclo';
  if (['cancelada', 'cancelado'].includes(baixo)) return 'Cancelada';
  return s;
}

export function recebeEnergia(statusPlanilha: string): boolean {
  return (STATUS_RECEBENDO as readonly string[]).includes(statusPlanilha);
}

/** Contrato ativo que saiu do rateio por ter saldo sobrando ("Ativa-S/-Rateio"). */
export function foraDoRateio(statusPlanilha: string): boolean {
  return statusPlanilha === STATUS_FORA_DO_RATEIO;
}

// ── Formatação ──────────────────────────────────────────────────────────────

/** Fração da geração da usina → texto em %: 0.0004 → "0,04%". */
export function formatarPercentRateio(fracao: number | null | undefined): string {
  if (fracao == null || !Number.isFinite(Number(fracao))) return '—';
  return `${(Number(fracao) * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

export function formatarKwh(n: number | null | undefined, casas = 0): string {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return Number(n).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
}

/** Mês seguinte: "AGO/2026" → "SET/2026". `null` se o mês não for reconhecido. */
export function mesSeguinte(mes: string | null | undefined): string | null {
  const ordinal = mesReferenciaParaOrdinal(mes);
  return ordinal == null ? null : rotuloDeOrdinal(ordinal + 1);
}

// ── % sugerido (modelo da planilha) ─────────────────────────────────────────

/**
 * Margem de erro do modelo — a célula única "Margem de erro: 15%" da aba
 * "Revisão". É o mesmo valor de `MARGEM_ERRO_PADRAO_PERCENT` (consumoHistorico.ts),
 * repetido aqui porque aquele arquivo importa o cliente Supabase.
 */
export const MARGEM_ERRO_MODELO = 0.15;

export type SugestaoPercent = {
  /** Fração da geração da usina, arredondada em 4 casas como na planilha. `null` = não dá para sugerir. */
  percent: number | null;
  consumoKwh: number | null;
  custoDisponibilidadeKwh: number;
  compensavelKwh: number | null;
  ajustadoKwh: number | null;
  geracaoMediaKwh: number | null;
  /** Por que não há sugestão — vai para a tela. */
  motivo?: string;
};

/**
 * Coluna "Rateio" da aba "Revisão":
 *   compensável = consumo − custo de disponibilidade (30 mono, 50 bi, 100 tri)
 *   ajustado    = compensável × (1 + margem de 15%)
 *   % da usina  = ARRED(ajustado ÷ geração média da usina; 4)
 *
 * O consumo é o operacional do sistema (observado nas faturas quando confiável,
 * senão o do cadastro) — o mesmo que a cobertura de saldo usa, para as duas
 * colunas do painel falarem da mesma UC.
 */
export function calcularPercentSugerido({
  consumoKwh,
  tipoLigacao,
  geracaoMediaKwh,
  margem = MARGEM_ERRO_MODELO,
}: {
  consumoKwh: number | null | undefined;
  tipoLigacao?: string | null;
  geracaoMediaKwh: number | null | undefined;
  margem?: number;
}): SugestaoPercent {
  const custoDisponibilidadeKwh = custoDisponibilidadeDe(tipoLigacao);
  const consumo = consumoKwh == null ? null : Number(consumoKwh);
  const geracao = geracaoMediaKwh == null ? null : Number(geracaoMediaKwh);
  const base: SugestaoPercent = {
    percent: null,
    consumoKwh: consumo,
    custoDisponibilidadeKwh,
    compensavelKwh: null,
    ajustadoKwh: null,
    geracaoMediaKwh: geracao,
  };

  if (consumo == null || !Number.isFinite(consumo) || consumo <= 0) {
    return { ...base, motivo: 'UC sem consumo para calcular.' };
  }
  const compensavelKwh = round2(consumo - custoDisponibilidadeKwh);
  if (compensavelKwh <= 0) {
    return {
      ...base,
      compensavelKwh,
      motivo: `Consumo abaixo do mínimo cobrado pela distribuidora (${custoDisponibilidadeKwh} kWh): não há o que compensar.`,
    };
  }
  const ajustadoKwh = round2(compensavelKwh * (1 + margem));
  if (geracao == null || !Number.isFinite(geracao) || geracao <= 0) {
    return { ...base, compensavelKwh, ajustadoKwh, motivo: 'Usina sem geração média registrada.' };
  }
  return {
    ...base,
    compensavelKwh,
    ajustadoKwh,
    percent: Math.round((ajustadoKwh / geracao) * 10000) / 10000,
  };
}

/** Média de geração da usina sobre os meses registrados (a planilha faz AVERAGE nos meses que usa). */
export function mediaGeracao(registros: { mes_referencia: string; geracao_kwh: number | string }[]): {
  mediaKwh: number | null;
  meses: number;
  ultimoMes: string | null;
} {
  const validos = registros
    .map(r => ({ mes: normalizarMesReferencia(r.mes_referencia), kwh: Number(r.geracao_kwh) }))
    .filter(r => Number.isFinite(r.kwh) && r.kwh >= 0);
  if (validos.length === 0) return { mediaKwh: null, meses: 0, ultimoMes: null };
  const soma = validos.reduce((s, r) => s + r.kwh, 0);
  const ultimo = validos.reduce<{ mes: string; ord: number } | null>((acc, r) => {
    const ord = mesReferenciaParaOrdinal(r.mes);
    if (ord == null) return acc;
    return !acc || ord > acc.ord ? { mes: r.mes, ord } : acc;
  }, null);
  return { mediaKwh: round2(soma / validos.length), meses: validos.length, ultimoMes: ultimo?.mes ?? null };
}

// ── Saldo ───────────────────────────────────────────────────────────────────

/**
 * Faixas de cobertura (meses de consumo que o saldo paga):
 * - falta: abaixo de 1 mês — entra no próximo ciclo sem lastro (alerta do CEO, 27/08).
 * - saudavel: 1 a 2 meses.
 * - atencao: acima de 2 e até 3 meses.
 * - excesso: acima de 3 — é onde o sistema pausa a UC (RAT-R11).
 */
export type FaixaCobertura = 'falta' | 'saudavel' | 'atencao' | 'excesso';

export function faixaDaCobertura(meses: number | null | undefined): FaixaCobertura | null {
  if (meses == null || !Number.isFinite(meses)) return null;
  if (meses < COBERTURA_MINIMA_SAUDAVEL_MESES) return 'falta';
  if (meses <= COBERTURA_MAXIMA_SAUDAVEL_MESES) return 'saudavel';
  if (meses <= LIMIAR_TRAVAR_MESES) return 'atencao';
  return 'excesso';
}

export type TendenciaSaldo = 'subindo' | 'caindo' | 'estavel';

/**
 * Direção do saldo nos últimos 3 meses com saldo lido. "Estável" absorve ruído:
 * variação de até 50 kWh ou 5% do saldo mais antigo da janela.
 */
export function tendenciaSaldo(historico: FaturaConsumoResumo[] | null | undefined): TendenciaSaldo | null {
  const comSaldo = (historico || []).filter(f => f.saldo_creditos_kwh != null && Number.isFinite(Number(f.saldo_creditos_kwh)));
  const janela = comSaldo.slice(0, 3);
  if (janela.length < 2) return null;
  const maisNovo = Number(janela[0].saldo_creditos_kwh);
  const maisAntigo = Number(janela[janela.length - 1].saldo_creditos_kwh);
  const delta = maisNovo - maisAntigo;
  if (Math.abs(delta) <= Math.max(50, Math.abs(maisAntigo) * 0.05)) return 'estavel';
  return delta > 0 ? 'subindo' : 'caindo';
}

// ── Divergências ────────────────────────────────────────────────────────────

export type CodigoDivergencia =
  | 'reincluir_no_rateio'
  | 'sem_cadastro'
  | 'status_diferente'
  | 'pausada_recebendo'
  | 'sem_credito_no_mes'
  | 'cancelada_com_saldo'
  | 'trocou_geradora'
  | 'sem_geradora'
  | 'varias_geradoras'
  | 'sem_fatura_recente';

export const DIVERGENCIAS: Record<CodigoDivergencia, { rotulo: string; descricao: string }> = {
  reincluir_no_rateio: {
    rotulo: 'Saldo acabando: reincluir no rateio',
    descricao:
      `A UC está fora do rateio (Ativa-S/-Rateio) e o saldo paga menos de ${COBERTURA_REINCLUIR_MESES} meses de consumo. ` +
      'A mudança enviada à distribuidora só vale no ciclo seguinte: peça a volta ao rateio no próximo envio.',
  },
  sem_cadastro: {
    rotulo: 'Sem cadastro',
    descricao: 'A UC está no rateio, mas não foi encontrada no cadastro de UCs (nem pelo número antigo, nem pelo novo).',
  },
  status_diferente: {
    rotulo: 'Status diferente',
    descricao: 'O status da UC no cadastro não é o mesmo da planilha do rateio.',
  },
  pausada_recebendo: {
    rotulo: 'Pausada, mas no rateio',
    descricao: 'O sistema pausou a UC (saldo de mais de 3 meses), mas ela continua recebendo crédito pelo rateio.',
  },
  sem_credito_no_mes: {
    rotulo: 'Sem crédito na fatura',
    descricao: 'A UC está ativa no rateio, mas a última fatura veio sem crédito recebido. Pode ser mês sem injeção: confira.',
  },
  cancelada_com_saldo: {
    rotulo: 'Cancelada com saldo',
    descricao: 'A UC está cancelada no rateio e ainda tem saldo de créditos parado na distribuidora.',
  },
  trocou_geradora: {
    rotulo: 'Trocou de geradora',
    descricao: 'Esta usina não aparecia para a UC no rateio anterior. A troca de geradora deve ser exceção.',
  },
  sem_geradora: {
    rotulo: 'Sem geradora',
    descricao: 'A UC recebe energia no rateio, mas a planilha não informa a UC geradora.',
  },
  varias_geradoras: {
    rotulo: 'Em mais de uma geradora',
    descricao: 'A UC aparece em mais de uma linha do rateio. O % sugerido não é calculado: a divisão entre usinas é manual.',
  },
  sem_fatura_recente: {
    rotulo: 'Sem fatura recente',
    descricao: 'Não há fatura desta UC no mês do rateio nem no anterior: o saldo mostrado pode estar desatualizado.',
  },
};

// ── Montagem das linhas ─────────────────────────────────────────────────────

export type ItemRateioPainel = {
  id: string;
  uc?: string | null;
  outro_numero_uc?: string | null;
  status?: string | null;
  nome?: string | null;
  cpf_cnpj?: string | null;
  uc_geradora?: string | null;
  percent_rateio?: number | string | null;
};

export type UcPainel = UcComConsumo & {
  id: number;
  numero_uc?: string | null;
  outro_numero_uc?: string | null;
  nome_cliente?: string | null;
  situacao?: string | null;
  tipo_ligacao?: string | null;
  rateio_travado_em?: string | null;
};

export type UgPainel = {
  id: string;
  nome: string;
  numero_uc_geradora?: string | null;
  numero_uc_geradora_antigo?: string | null;
  mediaGeracaoKwh: number | null;
  mesesGeracao: number;
};

export type UltimaFaturaPainel = {
  mes: string;
  consumoKwh: number | null;
  creditoRecebidoKwh: number | null;
};

/**
 * Saldo da fatura MAIS RECENTE QUE TROUXE SALDO — não necessariamente a última.
 * A fatura do mês às vezes chega sem o saldo lido; mostrar "—" ali enquanto o mês
 * anterior tem o número escondia a UC do alerta. O mês vai junto para a tela.
 */
export type SaldoPainel = { kwh: number; mes: string };

export type LinhaSaldoUc = {
  itemId: string;
  /** Número da UC como está na planilha (só dígitos). É a chave dos ajustes. */
  ucNumero: string;
  /** Número novo (12 dígitos) quando houver. */
  ucNovo: string | null;
  nome: string;
  cpfCnpj: string;
  statusPlanilha: string;
  statusCadastro: string | null;
  recebe: boolean;
  ucId: number | null;
  tipoLigacao: string | null;
  ugNumero: string | null;
  ugId: string | null;
  ugNome: string | null;
  percentVigente: number | null;
  percentAnterior: number | null;
  ugAnterior: string | null;
  sugestao: SugestaoPercent | null;
  /** Sugerido − vigente, em fração. `null` sem sugestão ou sem vigente. */
  diferencaSugestao: number | null;
  consumo: { valorKwh: number | null; fonte: FonteConsumo | null; suspeito: boolean };
  ultimaFatura: UltimaFaturaPainel | null;
  saldo: SaldoPainel | null;
  cobertura: CoberturaSaldo | null;
  faixa: FaixaCobertura | null;
  tendencia: TendenciaSaldo | null;
  divergencias: CodigoDivergencia[];
  historico: FaturaConsumoResumo[];
};

const num = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Geradora como a planilha trouxe. Número vira só dígitos (para comparar); texto
 * fica como está — há linhas com "Hsolar Crédito" no lugar do número, e espremer
 * isso para "HsolarCrédito" só confundiria quem lê.
 */
export function normalizarGeradora(valor: unknown): string | null {
  const bruto = String(valor ?? '').trim();
  if (!bruto) return null;
  return /[a-zà-ú]/i.test(bruto) ? bruto : limparUc(bruto) || null;
}

/** Chaves de busca de uma UC: número antigo e novo, só dígitos e sem zero à esquerda. */
function chavesUc(...numeros: (string | null | undefined)[]): string[] {
  return numeros.map(n => limparUc(n)).filter(Boolean);
}

/**
 * Usina de um número da planilha. O número ANTIGO explícito ganha do número
 * cadastrado: "Usina Morrinhos" foi cadastrada com 10036788990, que na planilha é
 * o número antigo da Vera Cruz 03 — sem essa ordem, a mesma UC cairia numa usina
 * sem geração registrada.
 */
export function resolverGeradora(numero: string | null | undefined, ugs: UgPainel[]): UgPainel | null {
  const chave = limparUc(numero);
  if (!chave) return null;
  return (
    ugs.find(u => limparUc(u.numero_uc_geradora_antigo) === chave) ||
    ugs.find(u => limparUc(u.numero_uc_geradora) === chave) ||
    null
  );
}

export function montarLinhasPainel({
  itens,
  itensAnteriores,
  ucs,
  historicoPorUc,
  ugs,
  mesRateio,
  hoje,
}: {
  itens: ItemRateioPainel[];
  itensAnteriores?: ItemRateioPainel[] | null;
  ucs: UcPainel[];
  historicoPorUc: Map<number, FaturaConsumoResumo[]>;
  ugs: UgPainel[];
  mesRateio: string;
  hoje?: Date;
}): LinhaSaldoUc[] {
  const ucPorChave = new Map<string, UcPainel>();
  for (const uc of ucs) {
    for (const chave of chavesUc(uc.numero_uc, uc.outro_numero_uc)) ucPorChave.set(chave, uc);
  }

  // Quantas linhas cada UC ocupa neste rateio (partição entre usinas).
  const linhasPorChave = new Map<string, number>();
  for (const item of itens) {
    const chave = chavesUc(item.uc, item.outro_numero_uc)[0];
    if (chave) linhasPorChave.set(chave, (linhasPorChave.get(chave) || 0) + 1);
  }

  // Todas as linhas da UC no rateio anterior — a UC pode estar em mais de uma usina.
  const anterioresPorChave = new Map<string, ItemRateioPainel[]>();
  for (const item of itensAnteriores || []) {
    for (const chave of chavesUc(item.uc, item.outro_numero_uc)) {
      const lista = anterioresPorChave.get(chave) || [];
      if (!lista.includes(item)) lista.push(item);
      anterioresPorChave.set(chave, lista);
    }
  }

  const ordinalRateio = mesReferenciaParaOrdinal(mesRateio);

  return itens.map(item => {
    const chaves = chavesUc(item.uc, item.outro_numero_uc);
    const ucNumero = chaves[0] || '';
    const uc = chaves.map(c => ucPorChave.get(c)).find(Boolean) || null;
    const statusPlanilha = normalizarStatusRateio(item.status);
    const recebe = recebeEnergia(statusPlanilha);
    const ug = resolverGeradora(item.uc_geradora, ugs);
    const ugNumero = normalizarGeradora(item.uc_geradora);
    const emVariasGeradoras = (linhasPorChave.get(ucNumero) || 0) > 1;
    // Compara com a MESMA usina no rateio anterior. Só quando a UC tem uma linha
    // agora e tinha uma antes vale comparar com a usina antiga (a troca simples);
    // numa UC dividida entre usinas, o % de outra usina não é "o de antes" desta.
    const anteriores = Array.from(new Set(chaves.flatMap(c => anterioresPorChave.get(c) || [])));
    const mesmaUsina = anteriores.find(a => normalizarGeradora(a.uc_geradora) === ugNumero) || null;
    const anterior = mesmaUsina || (!emVariasGeradoras && anteriores.length === 1 ? anteriores[0] : null);
    const historico = uc ? historicoPorUc.get(uc.id) || [] : [];
    const ultima = historico[0] || null;
    const ultimaFatura: UltimaFaturaPainel | null = ultima
      ? {
          mes: ultima.mes_referencia,
          consumoKwh: num(ultima.consumo_ativo_kwh),
          creditoRecebidoKwh: num(ultima.credito_recebido_kwh) ?? num(ultima.energia_injetada_kwh),
        }
      : null;
    const faturaComSaldo = historico.find(f => num(f.saldo_creditos_kwh) != null) || null;
    const saldo: SaldoPainel | null = faturaComSaldo
      ? { kwh: num(faturaComSaldo.saldo_creditos_kwh) as number, mes: faturaComSaldo.mes_referencia }
      : null;

    const consumoResolvido = uc ? consumoOperacionalDaUc(uc, hoje) : null;
    const consumo = {
      valorKwh: consumoResolvido?.valorKwh ?? null,
      fonte: consumoResolvido?.fonte ?? null,
      suspeito: Boolean(consumoResolvido?.suspeito),
    };

    // Cobertura pelo status DA PLANILHA: a pergunta é se a UC recebe crédito no
    // rateio. Pelo cadastro, uma UC pausada pelo sistema mas ainda no rateio
    // sumiria justamente do alerta que deveria disparar.
    //
    // A UC fora do rateio ("Ativa-S/-Rateio") também é medida (#6190): é o saldo
    // dela acabando que diz quando ela precisa voltar. Pelo cadastro, 27 das 73
    // de AGO/2026 estavam "Pausada" e a conta as descartaria — de novo, vale a planilha.
    const fora = foraDoRateio(statusPlanilha);
    const cobertura = uc && (recebe || fora)
      ? avaliarCoberturaSaldo(
          { uc: { ...uc, situacao: statusPlanilha }, saldoCreditosKwh: saldo?.kwh, mesReferencia: saldo?.mes ?? null },
          hoje
        )
      : null;

    const percentVigente = num(item.percent_rateio);
    const sugestao = recebe && !emVariasGeradoras
      ? calcularPercentSugerido({ consumoKwh: consumo.valorKwh, tipoLigacao: uc?.tipo_ligacao, geracaoMediaKwh: ug?.mediaGeracaoKwh })
      : null;
    const diferencaSugestao =
      sugestao?.percent != null && percentVigente != null ? Math.round((sugestao.percent - percentVigente) * 10000) / 10000 : null;

    const divergencias: CodigoDivergencia[] = [];
    // Primeiro da lista: é o único aviso que pede ação no próximo envio do rateio.
    if (fora && deveReincluirNoRateio(cobertura?.coberturaMeses)) divergencias.push('reincluir_no_rateio');
    if (!uc) divergencias.push('sem_cadastro');
    if (uc) {
      const statusCadastro = (uc.situacao || '').trim();
      const pausada = statusCadastro === 'Pausada' || Boolean(uc.rateio_travado_em);
      if (recebe && pausada) divergencias.push('pausada_recebendo');
      else if (statusCadastro !== statusPlanilha) divergencias.push('status_diferente');
    }
    // 1° Ciclo pode ainda não ter recebido o primeiro crédito — só cobra de quem já recebia.
    if (
      ultimaFatura &&
      (statusPlanilha === 'Ativa' || statusPlanilha === '2° Ciclo') &&
      !(Number(ultimaFatura.creditoRecebidoKwh) > 0)
    ) {
      divergencias.push('sem_credito_no_mes');
    }
    if (statusPlanilha === 'Cancelada' && Number(saldo?.kwh) > 0) divergencias.push('cancelada_com_saldo');
    const ugAnterior = normalizarGeradora(anterior?.uc_geradora);
    const tinhaUsinaAntes = anteriores.some(a => normalizarGeradora(a.uc_geradora));
    if (ugNumero && tinhaUsinaAntes && !mesmaUsina) divergencias.push('trocou_geradora');
    if (recebe && !ugNumero) divergencias.push('sem_geradora');
    if (emVariasGeradoras) divergencias.push('varias_geradoras');
    if (recebe && uc && ordinalRateio != null) {
      const ordinalFatura = mesReferenciaParaOrdinal(ultimaFatura?.mes);
      if (ordinalFatura == null || ordinalFatura < ordinalRateio - 1) divergencias.push('sem_fatura_recente');
    }

    return {
      itemId: item.id,
      ucNumero,
      ucNovo: limparUc(item.outro_numero_uc) || limparUc(uc?.numero_uc) || null,
      nome: (item.nome || uc?.nome_cliente || '').trim(),
      cpfCnpj: (item.cpf_cnpj || '').trim(),
      statusPlanilha,
      statusCadastro: uc?.situacao?.trim() || null,
      recebe,
      ucId: uc?.id ?? null,
      tipoLigacao: uc?.tipo_ligacao?.trim() || null,
      ugNumero,
      ugId: ug?.id ?? null,
      ugNome: ug?.nome ?? null,
      percentVigente,
      percentAnterior: num(anterior?.percent_rateio),
      ugAnterior,
      sugestao,
      diferencaSugestao,
      consumo,
      ultimaFatura,
      saldo,
      cobertura,
      faixa: faixaDaCobertura(cobertura?.coberturaMeses),
      tendencia: tendenciaSaldo(historico),
      divergencias,
      historico,
    };
  });
}

// ── Filtro e ordenação (a tela e o modal da usina usam a mesma) ─────────────

export type FiltroStatusSaldos = 'recebendo' | 'nao_recebe' | 'todos';
export type OrdenacaoSaldos = 'cobertura_desc' | 'cobertura_asc' | 'diferenca' | 'saldo_desc' | 'nome';

export type FiltrosSaldos = {
  busca: string;
  status: FiltroStatusSaldos;
  faixa: FaixaCobertura | 'sem_medida' | null;
  divergencia: CodigoDivergencia | 'qualquer' | '';
  /** Usinas (número como na planilha). Vazio = todas. */
  usinas: string[];
  soDiferenca: boolean;
  soAjusteAberto: boolean;
  ordenacao: OrdenacaoSaldos;
};

export const FILTROS_PADRAO: FiltrosSaldos = {
  busca: '',
  status: 'recebendo',
  faixa: null,
  divergencia: '',
  usinas: [],
  soDiferenca: false,
  soAjusteAberto: false,
  ordenacao: 'cobertura_desc',
};

/** Diferença mínima entre sugerido e vigente para contar como "há o que ajustar": 0,01 p.p. */
export const DIFERENCA_RELEVANTE = 0.0001;

export function filtrarEOrdenar(
  linhas: LinhaSaldoUc[],
  f: FiltrosSaldos,
  temAjusteAberto: (l: LinhaSaldoUc) => boolean = () => false
): LinhaSaldoUc[] {
  const termo = f.busca.trim().toLowerCase();
  const termoDigitos = termo.replace(/\D/g, '');
  const lista = linhas.filter(l => {
    if (f.status === 'recebendo' && !l.recebe) return false;
    if (f.status === 'nao_recebe' && l.recebe) return false;
    if (f.faixa === 'sem_medida' ? !l.recebe || l.faixa != null : f.faixa && l.faixa !== f.faixa) return false;
    if (f.divergencia === 'qualquer' && l.divergencias.length === 0) return false;
    if (f.divergencia && f.divergencia !== 'qualquer' && !l.divergencias.includes(f.divergencia)) return false;
    if (f.usinas.length > 0 && !f.usinas.includes(l.ugNumero || '')) return false;
    if (f.soDiferenca && !(l.diferencaSugestao != null && Math.abs(l.diferencaSugestao) >= DIFERENCA_RELEVANTE)) return false;
    if (f.soAjusteAberto && !temAjusteAberto(l)) return false;
    if (termo) {
      const bate =
        l.nome.toLowerCase().includes(termo) ||
        (termoDigitos.length >= 3 &&
          (l.ucNumero.includes(termoDigitos) || (l.ucNovo || '').includes(termoDigitos) || l.cpfCnpj.replace(/\D/g, '').includes(termoDigitos)));
      if (!bate) return false;
    }
    return true;
  });
  // Sem valor vai para o fim nas duas direções (e -Infinity − -Infinity daria NaN).
  const comparar = (x: number | null | undefined, y: number | null | undefined, desc: boolean) => {
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    return desc ? y - x : x - y;
  };
  return lista.sort((a, b) => {
    switch (f.ordenacao) {
      case 'cobertura_desc':
        return comparar(a.cobertura?.coberturaMeses, b.cobertura?.coberturaMeses, true) || a.nome.localeCompare(b.nome);
      case 'cobertura_asc':
        return comparar(a.cobertura?.coberturaMeses, b.cobertura?.coberturaMeses, false) || a.nome.localeCompare(b.nome);
      case 'diferenca':
        return comparar(
          a.diferencaSugestao == null ? null : Math.abs(a.diferencaSugestao),
          b.diferencaSugestao == null ? null : Math.abs(b.diferencaSugestao),
          true
        ) || a.nome.localeCompare(b.nome);
      case 'saldo_desc':
        return comparar(a.saldo?.kwh, b.saldo?.kwh, true) || a.nome.localeCompare(b.nome);
      default:
        return a.nome.localeCompare(b.nome);
    }
  });
}

// ── Indicadores e resumo por usina ──────────────────────────────────────────

export type IndicadoresPainel = {
  recebendo: number;
  falta: number;
  saudavel: number;
  atencao: number;
  excesso: number;
  semMedida: number;
  comDivergencia: number;
  /** Fora do rateio com saldo para menos de 2 meses: pedir a volta (#6190). */
  reincluir: number;
};

export function contarIndicadores(linhas: LinhaSaldoUc[]): IndicadoresPainel {
  const r: IndicadoresPainel = { recebendo: 0, falta: 0, saudavel: 0, atencao: 0, excesso: 0, semMedida: 0, comDivergencia: 0, reincluir: 0 };
  for (const l of linhas) {
    if (l.divergencias.length > 0) r.comDivergencia++;
    if (l.divergencias.includes('reincluir_no_rateio')) r.reincluir++;
    if (!l.recebe) continue;
    r.recebendo++;
    if (l.faixa) r[l.faixa]++;
    else r.semMedida++;
  }
  return r;
}

export type ResumoGeradora = {
  ugNumero: string;
  ugId: string | null;
  ugNome: string | null;
  mediaGeracaoKwh: number | null;
  mesesGeracao: number;
  ucsRecebendo: number;
  /** Soma dos % vigentes das UCs que recebem: quanto da usina já está alocado. */
  somaVigente: number;
  /** Soma dos % sugeridos (só UCs com sugestão). */
  somaSugerida: number;
  semSugestao: number;
};

/** Quadro do topo da aba "Revisão": capacidade usada e sobra de cada usina. */
export function resumirPorGeradora(linhas: LinhaSaldoUc[], ugs: UgPainel[]): ResumoGeradora[] {
  const mapa = new Map<string, ResumoGeradora>();
  for (const l of linhas) {
    if (!l.recebe || !l.ugNumero) continue;
    let r = mapa.get(l.ugNumero);
    if (!r) {
      const ug = resolverGeradora(l.ugNumero, ugs);
      r = {
        ugNumero: l.ugNumero,
        ugId: ug?.id ?? null,
        ugNome: ug?.nome ?? null,
        mediaGeracaoKwh: ug?.mediaGeracaoKwh ?? null,
        mesesGeracao: ug?.mesesGeracao ?? 0,
        ucsRecebendo: 0,
        somaVigente: 0,
        somaSugerida: 0,
        semSugestao: 0,
      };
      mapa.set(l.ugNumero, r);
    }
    r.ucsRecebendo++;
    r.somaVigente += l.percentVigente ?? 0;
    if (l.sugestao?.percent != null) r.somaSugerida += l.sugestao.percent;
    else r.semSugestao++;
  }
  return Array.from(mapa.values())
    .map(r => ({ ...r, somaVigente: Math.round(r.somaVigente * 10000) / 10000, somaSugerida: Math.round(r.somaSugerida * 10000) / 10000 }))
    .sort((a, b) => (a.ugNome || a.ugNumero).localeCompare(b.ugNome || b.ugNumero));
}

// ── Ajustes ─────────────────────────────────────────────────────────────────

export const TIPOS_AJUSTE: Record<RateioAjusteUc['tipo'], string> = {
  alterar_percentual: 'Alterar %',
  trocar_geradora: 'Trocar geradora',
  remanejar_saldo: 'Remanejar saldo',
  incluir: 'Incluir no rateio',
  excluir: 'Excluir do rateio',
  outro: 'Outro',
};

export const STATUS_AJUSTE: Record<RateioAjusteUc['status'], string> = {
  pendente: 'Pendente',
  enviado: 'Enviado à distribuidora',
  aplicado: 'Aplicado',
  cancelado: 'Cancelado',
};

export function ajusteAberto(a: Pick<RateioAjusteUc, 'status'>): boolean {
  return a.status === 'pendente' || a.status === 'enviado';
}

/**
 * O rateio aberto no painel já trouxe o que o ajuste pediu? Só responde quando o
 * rateio é do mês-alvo em diante: antes disso, "não aplicado" é o esperado.
 * - 'confirmado': a planilha trouxe o % / a usina pedida (ou tirou a UC, no caso de exclusão).
 * - 'nao_confirmado': o mês chegou e a planilha veio diferente.
 * - null: ainda não dá para conferir, ou o tipo não é conferível.
 */
export function conferirAjuste(
  ajuste: Pick<RateioAjusteUc, 'tipo' | 'percent_novo' | 'ug_nova' | 'mes_rateio_alvo' | 'mes_rateio_origem'>,
  linha: Pick<LinhaSaldoUc, 'percentVigente' | 'ugNumero' | 'statusPlanilha'> | null,
  mesRateio: string
): 'confirmado' | 'nao_confirmado' | null {
  const ordRateio = mesReferenciaParaOrdinal(mesRateio);
  const alvo = mesReferenciaParaOrdinal(ajuste.mes_rateio_alvo) ?? (mesReferenciaParaOrdinal(ajuste.mes_rateio_origem) ?? -Infinity) + 1;
  if (ordRateio == null || ordRateio < alvo) return null;

  if (ajuste.tipo === 'alterar_percentual') {
    if (ajuste.percent_novo == null || !linha || linha.percentVigente == null) return linha ? 'nao_confirmado' : null;
    return Math.abs(linha.percentVigente - Number(ajuste.percent_novo)) < 0.00005 ? 'confirmado' : 'nao_confirmado';
  }
  if (ajuste.tipo === 'trocar_geradora') {
    if (!linha) return null;
    return limparUc(ajuste.ug_nova) === linha.ugNumero ? 'confirmado' : 'nao_confirmado';
  }
  if (ajuste.tipo === 'excluir') {
    return !linha || linha.statusPlanilha === 'Cancelada' ? 'confirmado' : 'nao_confirmado';
  }
  if (ajuste.tipo === 'incluir') {
    // Voltou quando a planilha a traz recebendo energia; fora da planilha, não voltou.
    return linha && recebeEnergia(linha.statusPlanilha) ? 'confirmado' : 'nao_confirmado';
  }
  return null;
}

/** Texto inicial da observação do pedido "Incluir no rateio" aberto a partir do aviso. */
export function observacaoReinclusao(l: Pick<LinhaSaldoUc, 'cobertura' | 'saldo'>): string {
  if (!l.cobertura || !l.saldo) return '';
  const meses = l.cobertura.coberturaMeses.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
  return `Saldo de ${formatarKwh(l.saldo.kwh)} kWh (${l.saldo.mes}) paga ${meses} ${l.cobertura.coberturaMeses === 1 ? 'mês' : 'meses'} de consumo: voltar ao rateio.`;
}
